import { z } from 'zod';

/**
 * Facturation (CDC §10) : Krotime n'est pas le système fiscal. Après contrôle (validation du dossier), une entrée
 * « À facturer » est créée avec un instantané figé (client, lignes, totaux, acomptes). L'administrateur l'exporte vers
 * son logiciel de facturation (lot CSV), puis y reporte le numéro de facture : aucune facture n'est numérotée ni
 * émise par Krotime (pas de double comptabilité). Un connecteur (Facture.net) pourra transmettre directement les
 * entrées quand son API sera confirmée ; l'interface est la même.
 */
export const INVOICE_STATUSES = ['to_invoice', 'exported', 'invoiced', 'excluded', 'on_hold', 'to_regularize', 'regularized', 'cancelled'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];
export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  to_invoice: 'À facturer',
  exported: 'Exporté',
  invoiced: 'Facturé',
  excluded: 'Exclu',
  on_hold: 'En attente (dossier renvoyé)',
  to_regularize: 'À régulariser',
  regularized: 'Régularisé',
  cancelled: 'Annulé',
};
export const INVOICE_STATUS_TONES: Record<InvoiceStatus, 'info' | 'progress' | 'success' | 'neutral' | 'warning' | 'danger'> = {
  to_invoice: 'info',
  exported: 'progress',
  invoiced: 'success',
  excluded: 'neutral',
  on_hold: 'warning',
  to_regularize: 'danger',
  regularized: 'neutral',
  cancelled: 'neutral',
};

export interface InvoiceSnapshotLine {
  label: string;
  model: string | null;
  quantity: number;
  unit: string;
  unitPriceHtCents: number;
  vatRateBp: number;
  /** remise HT accordée sur la ligne (0 = aucune) */
  discountHtCents?: number;
  totalHtCents: number;
  totalTtcCents: number;
}

export interface InvoiceSnapshot {
  dossier: { reference: string; type: string; validatedAt: string; commercial: string | null; siteAddress: string | null };
  client: { name: string; kind: string; companyName: string | null; siret: string | null; address: string; email: string | null; phone: string | null };
  lines: InvoiceSnapshotLine[];
  totals: { htCents: number; vatCents: number; ttcCents: number };
  payments: { method: string; receivedOn: string | null; amountCents: number; deposit: boolean; status: string }[];
  paidCents: number;
}

export interface InvoiceEventEntry {
  at: string;
  byName: string;
  kind: string;
  comment: string | null;
}

export interface InvoiceEntryRecord {
  id: string;
  dossierId: string;
  reference: string;
  territoryId: string;
  clientName: string;
  status: InvoiceStatus;
  totalHtCents: number;
  totalTtcCents: number;
  paidCents: number;
  validatedAt: string;
  batchId: string | null;
  exportedAt: string | null;
  externalNumber: string | null;
  invoicedOn: string | null;
  note: string | null;
  snapshot: InvoiceSnapshot;
  events: InvoiceEventEntry[];
}

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date invalide');
export const invoiceExportInput = z.object({ ids: z.array(z.string().uuid()).min(1, 'Sélectionnez au moins un dossier').max(500) });
export const invoiceNumberInput = z.object({ externalNumber: z.string().trim().min(1, 'Numéro de facture obligatoire').max(60), invoicedOn: day });
export const invoiceExcludeInput = z.object({ reason: z.string().trim().min(3, 'Motif obligatoire').max(500) });
export const invoiceRegularizeInput = z.object({ comment: z.string().trim().min(3, 'Précisez la régularisation (ex. avoir n°…)').max(500) });

/** Actions allowed from each status (server and screens). */
export const INVOICE_ACTIONS: Record<'export' | 'invoiced' | 'exclude' | 'restore' | 'regularize', InvoiceStatus[]> = {
  export: ['to_invoice', 'exported'],
  invoiced: ['to_invoice', 'exported'],
  exclude: ['to_invoice', 'exported'],
  restore: ['excluded'],
  regularize: ['to_regularize'],
};
